/**
 * @fileoverview A program viewer's Students tab: the program's roster, and
 * uploading one.
 *
 * With the roster grant, every student on the program's classes: name, class,
 * attendance (with that grant) and recording consent. Never an address or a
 * contact field, never an instructor's notes. With roster upload, a class's
 * roster goes in through the administrator's own import, preview first;
 * students arrive with no recording consent. A viewer holding only the upload
 * grant sees the classes to upload into, and no names.
 */

import { useMemo, useState } from "react";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, ListRow, Tag, TextTabs } from "@/components/studio/primitives";
import { CSV_TYPE, ExportButton } from "@/components/studio/ExportMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImportRosterDialog from "@/components/org/ImportRosterDialog";
import ReportTable from "@/components/org/ReportTable";
import { attendedLabel, attendedRatio, plural } from "@/lib/viewerPortal";

const TEXT = "text-left font-sans text-[var(--text-primary)]";

// Spelled out for someone outside music teaching: the consent here is to the
// lessons being recorded, nothing else.
export const CONSENT_LABELS = {
  granted: "Recording consent",
  requested: "Recording consent asked",
  revoked: "Recording consent withdrawn",
  none: "No recording consent",
};

function columns(attendance) {
  const cols = [
    { id: "name", label: "Student", className: TEXT, sortValue: (s) => s.name, render: (s) => s.name },
    {
      id: "class",
      label: "Class",
      firstDir: "asc",
      className: TEXT,
      sortValue: (s) => (s.classes || []).map((c) => c.name).join(", "),
      render: (s) => (s.classes || []).map((c) => c.name).join(", ") || <span className="text-[var(--text-tertiary)]">—</span>,
    },
  ];
  if (attendance) {
    cols.push({ id: "attended", label: "Attended", className: "font-sans tabular-nums", sortValue: (s) => attendedRatio(s.attendance), render: (s) => attendedLabel(s.attendance) });
  }
  cols.push({
    id: "consent",
    label: "Recording consent",
    firstDir: "asc",
    className: "text-right font-sans",
    sortValue: (s) => s.consent_status || "",
    render: (s) => <Tag accent={s.consent_status === "granted"}>{CONSENT_LABELS[s.consent_status] || s.consent_status}</Tag>,
  });
  return cols;
}

/** Which class a roster goes into, when the tab is showing them all. */
function ChooseClass({ open, onOpenChange, classes, onChoose }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio max-w-md border-[var(--studio-divider)] bg-[var(--studio-base)] p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-serif text-xl text-[var(--text-primary)]">Upload a roster</DialogTitle>
          <p className="font-sans text-[14px] text-[var(--text-secondary)]">Choose the class the students are joining.</p>
        </DialogHeader>
        <div className="px-3 pb-4 pt-2">
          {classes.map((c) => (
            <ListRow
              key={c.class_id}
              title={c.name}
              subtitle={[c.instructor_name || "No instructor yet", plural(c.member_count ?? 0, "student")].join(" · ")}
              onPress={c.can_import ? () => onChoose(c) : undefined}
              data-testid={`viewer-upload-choose-${c.class_id}`}
              trailing={c.can_import ? undefined : <span className="font-sans text-[12px] text-[var(--text-tertiary)]">Needs an instructor</span>}
              className="rounded-[12px]"
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * @param {{ data: object, programId: string, onImported: () => void }} props
 *   `data` is `GET /org/viewer/programs/{id}`.
 */
export default function ViewerStudents({ data, programId, onImported }) {
  const { permissions = [], classes = [], students } = data;
  const canUpload = permissions.includes("roster_upload") && classes.some((c) => c.can_import);
  const attendance = permissions.includes("attendance");
  const [classId, setClassId] = useState("all");
  const [choosing, setChoosing] = useState(false);
  const [importing, setImporting] = useState(null);

  const shown = useMemo(
    () => (students || []).filter((s) => classId === "all" || (s.classes || []).some((c) => c.class_id === classId)),
    [students, classId],
  );
  const cols = useMemo(() => columns(attendance), [attendance]);
  const picked = classes.find((c) => c.class_id === classId);

  const upload = () => {
    if (picked) setImporting(picked);
    else if (classes.filter((c) => c.can_import).length === 1) setImporting(classes.find((c) => c.can_import));
    else setChoosing(true);
  };

  const exportItem = {
    url: `/org/viewer/programs/${programId}/export/students`,
    filename: `hans-students-${(data.program?.title || "program").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`,
    type: CSV_TYPE,
    label: "Export students",
    what: "students",
  };

  return (
    <div data-testid="viewer-students">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {classes.length > 1 && (
            <TextTabs
              testId="viewer-students-class"
              value={classId}
              onChange={setClassId}
              options={[
                { id: "all", label: "All classes", count: students ? students.length : undefined },
                ...classes.map((c) => ({ id: c.class_id, label: c.name, count: c.member_count })),
              ]}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canUpload && (
            <Button size="sm" icon="upload" onPress={upload} disabled={picked && !picked.can_import} data-testid="viewer-upload-roster">
              {picked ? `Upload a roster to ${picked.name}` : "Upload a roster"}
            </Button>
          )}
          {students && <ExportButton client={orgApi} errorMessage={getOrgErrorMessage} item={exportItem} testId="viewer-students-export" />}
        </div>
      </div>

      {students ? (
        <Card padding={0} className="mb-3">
          <ReportTable
            testId="viewer-students-table"
            columns={cols}
            rows={shown}
            rowKey={(s) => s.student_id}
            defaultSort={{ id: "name", dir: "asc" }}
            empty={(students || []).length === 0 ? "No students on this program’s rosters yet." : "No students in this class yet."}
          />
        </Card>
      ) : (
        <Card padding={0} className="mb-3" data-testid="viewer-upload-classes">
          {classes.map((c, idx) => (
            <ListRow
              key={c.class_id}
              borderBottom={idx < classes.length - 1}
              title={c.name}
              subtitle={[c.instructor_name || "No instructor yet", plural(c.member_count ?? 0, "student")].join(" · ")}
              trailing={
                c.can_import ? (
                  <Button size="sm" variant="ghost" icon="upload" onPress={() => setImporting(c)} data-testid={`viewer-import-${c.class_id}`}>
                    Upload roster
                  </Button>
                ) : undefined
              }
            />
          ))}
        </Card>
      )}

      <p className="max-w-3xl font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
        {students
          ? "Recording consent is a family's answer to whether lessons may be recorded. Contact details and instructors' notes are never shown here."
          : "Student names haven't been shared with you, but you can upload a class's roster."}
        {canUpload && " Uploaded students join with no recording consent until their families give it."}
      </p>

      <ChooseClass
        open={choosing}
        onOpenChange={setChoosing}
        classes={classes}
        onChoose={(c) => {
          setChoosing(false);
          setImporting(c);
        }}
      />
      <ImportRosterDialog
        open={Boolean(importing)}
        onOpenChange={(v) => !v && setImporting(null)}
        classId={importing?.class_id}
        className={importing?.name}
        endpoint={importing ? `/org/viewer/classes/${importing.class_id}/roster/import` : undefined}
        onImported={onImported}
      />
    </div>
  );
}
